import { Dashboard } from '@/api/information';
import { filterBySources } from '@/utils/menuSources';

const DEFAULT_PLATFORMS = [
  { platform: 'Booking',     originalPlatform: 'Booking',     otas: 'BOOKING',     score: 0, reviews: 0 },
  { platform: 'Trip',        originalPlatform: 'Trip',        otas: 'TRIP',        score: 0, reviews: 0 },
  { platform: 'Expedia',     originalPlatform: 'Expedia',     otas: 'EXPEDIA',     score: 0, reviews: 0 },
  { platform: 'Google',      originalPlatform: 'Google',      otas: 'GOOGLE',      score: 0, reviews: 0 },
  { platform: 'Tripadvisor', originalPlatform: 'Tripadvisor', otas: 'TRIPADVISOR', score: 0, reviews: 0 },
  { platform: 'agoda',       originalPlatform: 'agoda',       otas: 'AGODA',       score: 0, reviews: 0 },
  // --- New OTA sources (shown only when they have data for the hotel) ---
  { platform: 'HolidayCheck', originalPlatform: 'HolidayCheck', otas: 'HOLIDAYCHECK', score: 0, reviews: 0 },
  { platform: 'HRS',          originalPlatform: 'HRS',          otas: 'HRS',          score: 0, reviews: 0 },
  { platform: 'Lastminute',   originalPlatform: 'Lastminute',   otas: 'LASTMINUTE',   score: 0, reviews: 0 },
  { platform: 'Trivago',      originalPlatform: 'Trivago',      otas: 'TRIVAGO',      score: 0, reviews: 0 },
  { platform: 'Priceline',    originalPlatform: 'Priceline',    otas: 'PRICELINE',    score: 0, reviews: 0 },
  // Hotels.com (HOTELSCOM) and Hoteles.com (HOTELESCOM) intentionally omitted — both are
  // Expedia Group brand domains for the same property (same ho<id>) sharing Expedia's
  // review pool (97/122 hotels identical score+count). They just duplicate the Expedia
  // card, so Expedia is the single source for that pool.
  { platform: 'Rakuten',      originalPlatform: 'Rakuten',      otas: 'RAKUTEN',      score: 0, reviews: 0 },
  { platform: 'Traveloka',    originalPlatform: 'Traveloka',    otas: 'TRAVELOKA',    score: 0, reviews: 0 },
  { platform: 'Hostelworld',  originalPlatform: 'Hostelworld',  otas: 'HOSTELWORLD',  score: 0, reviews: 0 },
  { platform: 'Travelocity',  originalPlatform: 'Travelocity',  otas: 'TRAVELOCITY',  score: 0, reviews: 0 },
  { platform: 'Orbitz',       originalPlatform: 'Orbitz',       otas: 'ORBITZ',       score: 0, reviews: 0 },
  // Luxury Escapes — 10-point scale, same as Booking/Agoda (no /5 conversion anywhere).
  { platform: 'LuxuryEscapes', originalPlatform: 'LuxuryEscapes', otas: 'LUXURYESCAPES', score: 0, reviews: 0 },
  // Internal — 酒店自有渠道(IHG 内部问卷),**5 分制**,和 Google/Tripadvisor 一样需要 *2 归一。
  // 走 /hotel/dashboard 的 internalScore / internalReviews,和其它 OTA 同一条链路。
  { platform: 'Internal', originalPlatform: 'Internal', otas: 'INTERNAL', score: 0, reviews: 0 },
  // MakeMyTrip — **5-point scale** (1-5, stored as published), same as Google/Tripadvisor:
  // anything comparing it to the 1-10 AI score must *2 it (FIVE_POINT_SOURCES etc.).
  // ⚠️ platform/originalPlatform must upper-case to the otas code: the review page turns
  // the clicked card's `platform` into the source code with .toUpperCase()
  // (Reviews/index.vue handlePlatformsChange). 'MakeMyTrip' -> 'MAKEMYTRIP'.
  { platform: 'MakeMyTrip', originalPlatform: 'MakeMyTrip', otas: 'MAKEMYTRIP', score: 0, reviews: 0 },
  // hotel.de — 10-point scale, same as Booking/HRS (it is operated by HRS GmbH).
  // Same trap, worse: the display name "hotel.de" would upper-case to 'HOTEL.DE', not the
  // code, so platform is 'HotelDe' (-> 'HOTELDE'); the "hotel.de" label comes from
  // $t('OTAs.HOTELDE') / $t('OTAs.HotelDe'), never from this field.
  { platform: 'HotelDe', originalPlatform: 'HotelDe', otas: 'HOTELDE', score: 0, reviews: 0 },
];

const SCORE_KEYS = {
  BOOKING:     ['bookingScore',     'bookingReviews'],
  TRIP:        ['tripScore',        'tripReviews'],
  EXPEDIA:     ['expediaScore',     'expediaReviews'],
  GOOGLE:      ['googleScore',      'googleReviews'],
  TRIPADVISOR: ['tripadvisorScore', 'tripadvisorReviews'],
  AGODA:       ['agodaScore',       'agodaReviews'],
  // --- New OTA sources: keys match HotelReviewSummaryDTO camelCase fields ---
  HOLIDAYCHECK: ['holidaycheckScore', 'holidaycheckReviews'],
  HRS:          ['hrsScore',          'hrsReviews'],
  LASTMINUTE:   ['lastminuteScore',   'lastminuteReviews'],
  TRIVAGO:      ['trivagoScore',      'trivagoReviews'],
  PRICELINE:    ['pricelineScore',    'pricelineReviews'],
  RAKUTEN:      ['rakutenScore',      'rakutenReviews'],
  TRAVELOKA:    ['travelokaScore',    'travelokaReviews'],
  HOSTELWORLD:  ['hostelworldScore',  'hostelworldReviews'],
  TRAVELOCITY:  ['travelocityScore',  'travelocityReviews'],
  ORBITZ:       ['orbitzScore',       'orbitzReviews'],
  LUXURYESCAPES: ['luxuryescapesScore', 'luxuryescapesReviews'],
  INTERNAL:      ['internalScore',      'internalReviews'],
  MAKEMYTRIP:    ['makemytripScore',    'makemytripReviews'],
  HOTELDE:       ['hoteldeScore',       'hoteldeReviews'],
};

/** Dedupe concurrent /hotel/dashboard calls for the same hotelId */
const dashboardRequestByHotel = new Map();

function fetchDashboardForHotel(hotelId) {
  if (!hotelId) return Promise.resolve(null);
  if (dashboardRequestByHotel.has(hotelId)) {
    return dashboardRequestByHotel.get(hotelId);
  }
  const request = Dashboard({ hotelId }).finally(() => {
    dashboardRequestByHotel.delete(hotelId);
  });
  dashboardRequestByHotel.set(hotelId, request);
  return request;
}

function buildPlatformsFromReviewSummary(reviewSummary, sources) {
  const s = reviewSummary || {};
  // Always build from the full platform list so a platform that gains data later
  // re-appears (don't derive from the previously-filtered ratingInfos).
  const withData = DEFAULT_PLATFORMS
    .map((p) => {
      const keys = SCORE_KEYS[p.otas];
      return {
        ...p,
        score: keys ? (s[keys[0]] ?? 0) : 0,
        reviews: keys ? (s[keys[1]] ?? 0) : 0,
      };
    })
    // Only show an OTA that actually has data for this hotel (score or reviews > 0).
    // Platforms with no URL / no collected data are hidden.
    .filter((p) => Number(p.score) > 0 || Number(p.reviews) > 0);
  // Further restrict to the platforms this hotel actually has enabled
  // (hotel.menu.sources from /auth/entities). sources == null keeps old behavior.
  return filterBySources(withData, sources);
}

export default {
  namespaced: true, // 启用命名空间
  state: {
    mediaSwitch: true,
    ratingInfos: [],
    // 平台列表已经成功拉到过的酒店 id。列表可以合法地为空（酒店没有 OTA 数据 / 没开来源），
    // 页面靠它区分「还没加载」和「加载完了但没有平台」，不能只看 ratingInfos.length。
    ratingInfosLoadedFor: null,
    selectedPlatforms: ["BOOKING"],
    // 新增：存储优势和改进项数据
    strengths: [], // Top 5 strengths数据
    improvements: [], // Top 5 areas for improvement数据
  },
  getters: {
    // 获取选中的平台
    selectedPlatforms: (state) => state.selectedPlatforms,
    // 可以添加更多getter
    isMediaOn: (state) => state.mediaSwitch,
    ratingCount: (state) => state.ratingInfos.length,
    strengths: (state) => state.strengths, // 新增
    improvements: (state) => state.improvements, // 新增
  },
  mutations: {
    // 更新媒体开关状态
    updateSwitchState(state, value) {
      state.mediaSwitch = value;
    },
    // 设置评分信息
    SET_RATING_INFOS(state, payload) {
      state.ratingInfos = payload;
    },
    SET_RATING_INFOS_LOADED_FOR(state, hotelId) {
      state.ratingInfosLoadedFor = hotelId;
    },
    // 设置选中的平台
    SET_SELECTED_PLATFORMS(state, platforms) {
      state.selectedPlatforms = platforms?platforms:["BOOKING"];
    },
    SET_STRENGTHS(state, data) {
      state.strengths = data; // 直接存原始数据（包括null/undefined）
    },
    SET_IMPROVEMENTS(state, data) {
      state.improvements = data;
    },
    // 清空所有状态（如果需要）
    CLEAR_ALL_STATE(state) {
      state.mediaSwitch = true;
      state.ratingInfos = [];
      state.ratingInfosLoadedFor = null;
      state.selectedPlatforms = [];
      // 新增：清空优势和改进项数据
      state.strengths = [];
      state.improvements = [];
    },
  },
  actions: {
    // 更新选中的平台
    updateSelectedPlatforms({ commit }, platforms) {
      commit("SET_SELECTED_PLATFORMS", platforms);
      console.log("vuex中home存储的平台名", platforms);
    },
    // 可以添加更多action
    toggleMediaSwitch({ commit, state }) {
      commit("updateSwitchState", !state.mediaSwitch);
    },
    /** Single /hotel/dashboard fetch; concurrent calls for the same hotelId share one request. */
    fetchHotelDashboard(_, hotelId) {
      return fetchDashboardForHotel(hotelId);
    },
    // Populate ratingInfos from /hotel/dashboard. Pass hotelId only, or
    // { hotelId, reviewSummary } when the caller already fetched dashboard once.
    async loadRatingInfos({ commit, rootState }, payload) {
      const hotelId = typeof payload === "object" && payload !== null
        ? payload.hotelId
        : payload;
      const reviewSummary = typeof payload === "object" && payload !== null
        ? payload.reviewSummary
        : undefined;
      if (!hotelId) return;

      const hotel = (rootState.hotel.hotelList || []).find((h) => h.hotelId === hotelId);
      const sources = hotel && hotel.menu ? hotel.menu.sources : null;

      let platforms = buildPlatformsFromReviewSummary(reviewSummary, sources);
      let loaded = reviewSummary !== undefined;
      if (reviewSummary === undefined) {
        try {
          const res = await fetchDashboardForHotel(hotelId);
          platforms = buildPlatformsFromReviewSummary(res && res.reviewSummary, sources);
          loaded = true;
        } catch (e) {
          // API failed — still commit the platform skeleton so icons render. Not marked
          // loaded: pages keep their loading / retry state instead of showing "no platforms".
        }
      }
      commit("SET_RATING_INFOS", platforms);
      if (loaded) commit("SET_RATING_INFOS_LOADED_FOR", hotelId);
    },
    // 新增：更新优势数据
    updateStrengths({ commit }, data) {
      commit("SET_STRENGTHS", data);
    },
    // 新增：更新改进项数据
    updateImprovements({ commit }, data) {
      commit("SET_IMPROVEMENTS", data);
    },
  },
};
